import { forwardRef } from 'react';
import { DbProduct, DbCategory } from '@/hooks/useMenuData';
import { useCurrency } from '@/hooks/useCurrency';

interface MenuSectionProps {
  category: DbCategory;
  items: DbProduct[];
  onSelectItem: (item: DbProduct) => void;
}

const placeholderImg = 'https://readdy.ai/api/search-image?query=delicious%20food%20dish%20on%20white%20plate%2C%20professional%20food%20photography%2C%20clean%20background%2C%20appetizing%20presentation%2C%20warm%20lighting&width=600&height=400&seq=placeholder-menu&orientation=landscape';

const MenuSection = forwardRef<HTMLDivElement, MenuSectionProps>(({ category, items, onSelectItem }, ref) => {
  const { format } = useCurrency();

  return (
    <div ref={ref} className="mb-10">
      {/* Category Header */}
      <div className="flex items-center gap-3 mb-5 pb-3 border-b border-stone-100">
        <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
          <i className={`${category.icon} text-amber-600 text-lg`}></i>
        </div>
        <div>
          <h2 className="text-stone-900 font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
            {category.name_de}
          </h2>
          <p className="text-stone-400 text-xs mt-0.5">{items.length} Artikel</p>
        </div>
      </div>

      {/* Items */}
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelectItem(item)}
            className="group flex items-center gap-4 bg-white hover:bg-amber-50/50 border border-stone-100 hover:border-amber-200 rounded-2xl p-3 text-left transition-all duration-200 cursor-pointer w-full"
          >
            {/* Image */}
            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 relative">
              <img
                src={item.image_url || placeholderImg}
                alt={item.name_de}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              {item.badge_de && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-md leading-tight">
                  {item.badge_de}
                </span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-stone-900 font-semibold text-sm leading-tight">{item.name_de}</h3>
                <span className="text-amber-600 font-bold text-sm whitespace-nowrap flex-shrink-0">
                  {format(Number(item.price))}
                </span>
              </div>
              {item.description_de && (
                <p className="text-stone-400 text-xs leading-relaxed mt-1 line-clamp-2">{item.description_de}</p>
              )}
              {item.extras.length > 0 && (
                <div className="flex items-center gap-1 mt-2">
                  <i className="ri-settings-3-line text-stone-300 text-xs"></i>
                  <span className="text-stone-300 text-xs">Anpassbar</span>
                </div>
              )}
            </div>

            {/* Add button */}
            <div className="w-9 h-9 flex items-center justify-center bg-stone-100 group-hover:bg-amber-500 rounded-full flex-shrink-0 transition-all duration-200">
              <i className="ri-add-line text-stone-500 group-hover:text-white text-base transition-colors"></i>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
});

MenuSection.displayName = 'MenuSection';

export default MenuSection;
